import { Icon, Tooltip, Typography } from "@/components/atoms";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { clsx } from "@/utils";

/**
 * Initial selection for the Vehicle Type multi-select. Prefers the many-to-many
 * `vehicleTypes` array; falls back to the legacy 1-to-1 `vehicleType` for
 * vehicles served by an older API response.
 */
export const getInitialVehicleTypeUuids = (vehicle: Vehicle): string[] => {
  if (vehicle.vehicleTypes && vehicle.vehicleTypes.length > 0) {
    return vehicle.vehicleTypes.map((t) => t.uuid);
  }
  return vehicle.vehicleType ? [vehicle.vehicleType.uuid] : [];
};

/**
 * Shared "Vehicle Type" table cell used by every vehicle tab. Two or more types
 * collapse into a "+N Vehicle Types" count. A single attached type shows its
 * name by default, or "1 Vehicle Type" when `singleAsCountLink` is set (the
 * table design counts every row; detail cards keep the name). When
 * `onShowTypes` is provided the count is a clickable link that opens the
 * vehicle-types modal; when omitted (e.g. read-only legacy tables) it renders as
 * plain text. Prefers the many-to-many `vehicleTypes` set, falling back to the
 * legacy single type. `emptyFallback` is shown when none are attached (default "-").
 * `singleTypeClassName` styles the single-type name (e.g. to match a detail card).
 */
export const renderVehicleTypeCell = (
  vehicle: Vehicle,
  onShowTypes?: (vehicle: Vehicle) => void,
  emptyFallback: JSX.Element = <Typography className="line-clamp-1">-</Typography>,
  singleTypeClassName?: string,
  singleAsCountLink = false
): JSX.Element => {
  const types = vehicle.vehicleTypes?.length ? vehicle.vehicleTypes : vehicle.vehicleType ? [vehicle.vehicleType] : [];

  if (types.length === 0) {
    return emptyFallback;
  }

  if (types.length === 1 && !singleAsCountLink) {
    return <Typography className={clsx("line-clamp-1", singleTypeClassName)}>{types[0].name}</Typography>;
  }

  const label = types.length === 1 ? "1 Vehicle Type" : `+${types.length} Vehicle Types`;

  if (!onShowTypes) {
    return <Typography className="line-clamp-1">{label}</Typography>;
  }

  return (
    <button type="button" onClick={() => onShowTypes(vehicle)} className="self-start text-left text-info underline">
      {label}
    </button>
  );
};

export const renderColumnHeaderWithTooltip = (label: string, tooltip: string | JSX.Element, subLabel?: string) => {
  const ColumnHeader = () => (
    <div className="flex flex-col">
      <div className="flex items-center gap-1">
        {label}
        <Tooltip content={tooltip} interactive={typeof tooltip !== "string"}>
          <Icon name="InfoCircle" size="sm" variant="Bold" className="text-neutral-dark-gray" />
        </Tooltip>
      </div>
      {subLabel && (
        <Typography variant="small-regular" className="font-normal text-neutral-dark-gray">
          {subLabel}
        </Typography>
      )}
    </div>
  );
  ColumnHeader.displayName = `ColumnHeader(${label})`;
  return ColumnHeader;
};
